import React, { useMemo } from 'react'
import type { VirtualItem } from '@tanstack/react-virtual'
import { ROW_H, type ColumnId } from '../lib/constants'
import { formatPredecessors, type LocalLink } from '../lib/predecessors'
import type { Tree, VisibleRow } from '../lib/tree'
import type { ScheduleActivity } from '../types'
import type { CellMove } from './cells/CellEditor'
import GanttRow, { type ForeignPredecessor } from './GanttRow'

export { COLUMN_ORDER, columnLabelKey, columnWidth, gridWidthFor, isNumericColumn } from './gantt-columns'

interface Props {
  rows: VisibleRow<ScheduleActivity>[]
  virtualItems: VirtualItem[]
  columns: ColumnId[]
  columnWidths: Record<ColumnId, number>
  gridWidth: number
  selectedIds: string[]
  focusedId: string | null
  readOnly: boolean
  tree: Tree<ScheduleActivity>
  predecessorsByActivity: Map<string, { local: LocalLink[]; foreign: ForeignPredecessor[] }>
  editingCell: { activityId: string; column: ColumnId } | null
  pending: Map<string, { column: ColumnId; value: string }>
  onToggle: (id: string) => void
  onClick: (id: string, e: React.MouseEvent) => void
  onDoubleClick: (id: string, column: ColumnId) => void
  onContextMenu: (id: string, e: React.MouseEvent) => void
  onCommitCell: (id: string, column: ColumnId, raw: string, move: CellMove) => void
  onCancelCell: () => void
}

/**
 * The frozen pane. It sits in the flow directly under the sticky header row, so
 * a virtual row's `top` is simply `index * ROW_H`; `position: sticky; left: 0`
 * keeps it in place while the timeline scrolls horizontally.
 */
const GanttGrid: React.FC<Props> = ({
  rows, virtualItems, columns, columnWidths, gridWidth, selectedIds, focusedId, readOnly, tree, predecessorsByActivity, editingCell, pending,
  onToggle, onClick, onDoubleClick, onContextMenu, onCommitCell, onCancelCell,
}) => {
  const selected = useMemo(() => {
    const set = new Set<string>()
    selectedIds.forEach((id) => set.add(id))
    return set
  }, [selectedIds])

  return (
    <div
      className="sticky left-0 z-20 overflow-hidden bg-background border-r"
      // ui-drift: dynamic
      style={{ width: gridWidth, height: rows.length * ROW_H }}
    >
      {virtualItems.map((vi) => {
        const row = rows[vi.index]
        if (!row) return null
        const activity = row.node.activity
        const preds = predecessorsByActivity.get(activity.id)
        const p = pending.get(activity.id)
        return (
          <GanttRow
            key={activity.id}
            activity={activity}
            depth={row.node.depth}
            rowNumber={row.node.rowNumber}
            hasChildren={row.hasChildren}
            collapsed={row.collapsed}
            selected={selected.has(activity.id)}
            focused={focusedId === activity.id}
            top={vi.index * ROW_H}
            gridWidth={gridWidth}
            columns={columns}
            columnWidths={columnWidths}
            readOnly={readOnly}
            tree={tree}
            predecessorsText={preds ? formatPredecessors(preds.local, tree) : ''}
            foreignPredecessors={preds?.foreign}
            editingColumn={editingCell && editingCell.activityId === activity.id ? editingCell.column : null}
            pendingColumn={p ? p.column : null}
            pendingValue={p ? p.value : null}
            onToggle={onToggle}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
            onCommitCell={onCommitCell}
            onCancelCell={onCancelCell}
          />
        )
      })}
    </div>
  )
}

export default React.memo(GanttGrid)
